import { useState } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { KpiStrip, MetricCard, Panel } from '@/components/erp/primitives'
import { BucketAxis, ChartFrame, RankedBars, Sparkbars } from '@/shared/ui/charts'
import { ErrorStrip } from '@/shared/ui/states'
import { cn } from '@/lib/utils'
import { outcomeTone, type ReportModel } from './report'

export const RANGES = [
  { id: '24h', label: '24 hours', hours: 24, bucket: 'HOUR' as const },
  { id: '7d', label: '7 days', hours: 24 * 7, bucket: 'DAY' as const },
  { id: '30d', label: '30 days', hours: 24 * 30, bucket: 'DAY' as const },
]

export type RangeId = (typeof RANGES)[number]['id']

/** Turns a range id into the `from`/`to`/`bucket` the report endpoints take. */
export function rangeParams(id: RangeId) {
  const r = RANGES.find((x) => x.id === id) ?? RANGES[1]!
  const to = new Date()
  const from = new Date(to.getTime() - r.hours * 3600_000)
  return { from: from.toISOString(), to: to.toISOString(), bucket: r.bucket }
}

export function RangePicker({
  value, onChange, onRefresh, busy,
}: {
  value: RangeId
  onChange: (id: RangeId) => void
  onRefresh?: () => void
  busy?: boolean
}) {
  return (
    <div className="flex items-center gap-1">
      {RANGES.map((r) => (
        <button
          key={r.id}
          type="button"
          onClick={() => onChange(r.id)}
          className={cn(
            'h-7 px-2 rounded-md text-erp-xs border',
            value === r.id
              ? 'border-chocolate/50 bg-chocolate-soft/50 text-chocolate font-medium'
              : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50',
          )}
        >
          {r.label}
        </button>
      ))}
      {onRefresh && (
        <button type="button" className="erp-btn h-7 px-2" onClick={onRefresh} aria-label="Refresh report">
          <RefreshCw className={cn('h-3 w-3', busy && 'animate-spin')} />
        </button>
      )}
    </div>
  )
}

/**
 * The report body: a KPI row, small multiples for volume and failures, and the
 * ranked breakdowns.
 *
 * Volume and failures are two panels sharing an x domain rather than one stacked
 * chart. That is not a stylistic preference — see the note at the top of
 * `shared/ui/charts.tsx`: the design system's hues cannot reliably encode two
 * categories, so the split is done with position instead of colour.
 */
export function ReportBody({
  model,
  error,
  loading,
  emptyHint,
}: {
  model: ReportModel | null
  error?: string | null
  loading?: boolean
  emptyHint?: string
}) {
  if (error) {
    return (
      <Panel>
        <ErrorStrip message={error} />
        <p className="text-erp-xs text-muted-foreground mt-2">
          The report could not be loaded. The rest of this page still works — configuration and
          credentials are read from a different endpoint.
        </p>
      </Panel>
    )
  }

  if (!model) {
    return (
      <Panel>
        <div className="py-10 text-center text-erp-sm text-muted-foreground">
          {loading ? 'Loading the report…' : 'No report yet.'}
        </div>
      </Panel>
    )
  }

  return (
    <div className="space-y-3">
      <KpiStrip>
        {model.tiles.map((t) => (
          <MetricCard key={t.label} label={t.label} value={t.value} sub={t.sub} accent={t.accent} />
        ))}
      </KpiStrip>

      {model.empty && (
        <Panel>
          <div className="py-6 text-center space-y-1">
            <div className="text-erp-sm font-medium">Nothing recorded in this window</div>
            <p className="text-erp-xs text-muted-foreground max-w-md mx-auto">
              {emptyHint ??
                'Either nothing has used this yet, or calls are not being recorded. Widen the range before concluding it is idle.'}
            </p>
          </div>
        </Panel>
      )}

      {!model.empty && (
        <Panel bodyClassName="space-y-4">
          <ChartFrame title="Requests" description="Total calls per bucket">
            <Sparkbars points={model.volume} height={80} />
          </ChartFrame>
          <ChartFrame title="Failures" description="Same buckets, same order">
            <Sparkbars points={model.failures} height={48} emptyMessage="No failures." />
            <BucketAxis points={model.failures} />
          </ChartFrame>
          {model.latency.some((p) => p.value > 0) && (
            <ChartFrame title="p95 latency" description="Milliseconds">
              <Sparkbars points={model.latency} height={48} formatValue={(n) => `${n} ms`} />
            </ChartFrame>
          )}
        </Panel>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        {model.breakdowns.map((b) => (
          <Panel key={b.title} title={b.title} description={b.description} dense>
            <RankedBars
              points={b.points}
              tone={b.statusTone ? outcomeTone : undefined}
              emptyMessage="Nothing in this window."
            />
          </Panel>
        ))}
      </div>
    </div>
  )
}

/**
 * A one-off warning about a backend defect that makes a screen look broken.
 *
 * Shown rather than swallowed: an operator who sees an empty report and no
 * explanation will assume the integration is idle, which is a worse outcome than
 * being told the endpoint is faulty.
 */
export function KnownIssue({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(true)
  if (!open) return null
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50/70 dark:bg-amber-950/30 dark:border-amber-900 px-3 py-2 flex items-start gap-2">
      <AlertTriangle className="h-3.5 w-3.5 text-amber-700 dark:text-amber-400 shrink-0 mt-0.5" />
      <div className="text-erp-xs text-amber-900 dark:text-amber-200 min-w-0 flex-1">{children}</div>
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="text-erp-xs underline text-amber-800 dark:text-amber-300 shrink-0"
      >
        dismiss
      </button>
    </div>
  )
}
